import {useAppTranslation} from "../../services/i18n";
import {Link as RouterLink} from "react-router-dom";
import {useAppDispatch, useAppSelector} from "../../store";
import {selectCodebooks} from "../../store/selectors";
import * as React from "react";
import {CSSProperties, Fragment, useCallback, useEffect, useMemo, useState} from "react";
import {
    GetEventPartyListUsingGETStatusesEnum,
    JsonEventCateringData,
    JsonEventCateringDay,
    JsonEventDataFoodServiceTypesEnum,
    JsonEventDayInfo,
    JsonEventPartyInfo,
    JsonGroupInfo,
    JsonPlaceFoodServicesEnum,
    JsonPlaceInfo
} from "../../generated-api";
import {createOption, createOptions, FAKE_VALUE_ALL, FAKE_VALUE_EMPTY, OptionValue} from "../../model/form";
import {dateToGuiAs} from "../../helpers/date";
import {getApiResult} from "../../helpers/api";
import {Card, CardContent, CardHeader, Grid, LinearProgress, Link, Table, TableBody, TableCell, TableHead, TableRow} from "@mui/material";
import {foodServiceOptions, foodServiceProps} from "../../model/place";
import {fetchPlacesOnline} from "../../store/places";
import {fetchEventPartiesOnline} from "../../store/eventParties";
import {CodebookState} from "../../store/codebooks";
import {fetchEvent} from "../../store/events";
import {localCompare} from "../DataGrid";
import {fetchGroups} from "../../store/groups";

const cateringStyle = {
    height: '100%',
    '& .data-grid tbody > tr > td > span': {
        padding: '4px 0px'
    }
}

type CateringPlaceGroup = {
    groupId: number,
    title: string,
    days: {
        [key in number]: {
            items: JsonEventPartyInfo[],
            limits: JsonEventCateringDay[]
        }
    },
}

type CateringPlace = {
    placeId: number,
    title: string,
    capacity?: number,
    groups: CateringPlaceGroup[]
}

type EventFoodPlaceProps = {
    eventId: number,
    pp: CateringPlace,
    days: JsonEventDayInfo[],
    foodServiceTypes: OptionValue[],
    groups: JsonGroupInfo[]
}

const EventFoodPlace = (props: EventFoodPlaceProps) => {
    const {eventId, pp, days, foodServiceTypes, groups} = props;

    const t = useAppTranslation();

    const getCateringInfo = useCallback((g: CateringPlaceGroup, fs: OptionValue, day: JsonEventDayInfo) => {
        const dayNo = day.dayNo!;
        const serviceType = fs.value as JsonPlaceFoodServicesEnum;
        const foodProp = foodServiceProps[serviceType];

        const dd = g.days[dayNo]?.items
            ?.map((ep) => ep.eventDayDetails?.find((dd) => dd.dayNo === dayNo
                && ((pp.placeId === FAKE_VALUE_ALL && !!dd[foodProp]) || dd[foodProp] === pp.placeId)))
            ?.filter(dd => !!dd);
        const eventCatering = g.days[dayNo]?.limits
            ?.find(cd => cd.eventDayId === day.eventDayId
                && cd.foodType === fs.value
                && (cd.placeId === pp.placeId || pp.placeId === FAKE_VALUE_ALL)
            );

        const total = dd?.reduce((total, dd) => total + (dd?.foodCnt?.[foodProp] || 1), 0);
        const used = dd?.filter(dd => dd?.foodData?.log?.find(l => l.serviceType as any === serviceType as any)).length || 0;
        return {total, used, eventCatering}
    }, [pp.placeId]);

    return useMemo(() => {
        const totals: { [key in string]: number } = {};
        const useds: { [key in string]: number } = {};
        const limits: { [key in string]: number } = {};

        const foodCountStyle: CSSProperties = {textAlign: 'center'};
        const rows = pp.groups.filter(g => Object.keys(g.days).some(k => !!g.days[+k]?.items?.length)).map((g) => {
            return <TableRow key={g.groupId}>
                <TableCell><span><strong>{g.title}</strong></span></TableCell>
                {days?.map((d, i) => {
                    return foodServiceTypes.map((fs) => {
                        const dayNo = d.dayNo;
                        if (!dayNo) {
                            return null;
                        }
                        const {total, used, eventCatering} = getCateringInfo(g, fs, d);

                        const group = groups.find(group => group.groupId === g.groupId);
                        const limit = eventCatering?.foodCnt || 0;
                        let limitUsed = used || 0;
                        group?.settings?.managedGroupIds?.forEach((managedId) => {
                            const managed = pp.groups.find(group => group.groupId === managedId);
                            if (managed) {
                                const {used} = getCateringInfo(managed, fs, d);
                                limitUsed += used;
                            }
                        });

                        const k = d.dayNo + '-' + fs.value;
                        if (!totals[k]) {
                            totals[k] = 0;
                            useds[k] = 0;
                            limits[k] = 0;
                        }
                        if (total) {
                            totals[k] += total;
                        }
                        if (used) {
                            useds[k] += used;
                        }
                        if (limit) {
                            limits[k] += limit;
                        }

                        const cn = i % 2 > 0 ? 'high-col' : undefined;
                        return <Fragment key={k}>
                            <TableCell style={foodCountStyle} className={cn}><span>{!!total && <Link underline={'hover'}
                                to={`/event-parties/?eventId=${eventId}&eventDay=${d.dayNo}&groupIds=${g.groupId}&foodIds=${pp.placeId}`}
                                component={RouterLink}>{total}</Link>}{!!limit && <><br/><span style={{color: 'gray'}}>{limit}</span></>}</span>
                            </TableCell>
                            <TableCell style={foodCountStyle} className={cn}>
                                <span>{!!used ? <span style={{color: 'var(--green)'}}>{used}</span> : null}{!!limit && <><br/>{limit - limitUsed}</>}</span>
                            </TableCell>
                        </Fragment>;
                    })
                })}
            </TableRow>
        });
        if (rows.length <= 0) {
            return null;
        }

        if (rows.length > 1) {
            rows.push(<TableRow key={'total'}>
                <TableCell><span><strong>{t('Celkem')}</strong></span></TableCell>
                {days?.map((d, i) => {
                    if (!d.dayNo) {
                        return null;
                    }
                    return foodServiceTypes.map((fs) => {
                        const k = d.dayNo + '-' + fs.value;
                        const total = totals[k] || 0;
                        const used = useds[k] || 0;
                        const limit = limits[k] || 0;
                        const cn = i % 2 > 0 ? 'high-col' : undefined;
                        return <Fragment key={k}>
                            <TableCell style={foodCountStyle} className={cn}><span><strong>{!!total && <Link underline={'hover'}
                                to={`/event-parties/?eventId=${eventId}&eventDay=${d.dayNo}&foodIds=${pp.placeId}`}
                                component={RouterLink}>{total}</Link>}</strong><br/><span style={{color: 'gray'}}>{limit}</span></span>
                            </TableCell>
                            <TableCell style={foodCountStyle} className={cn}>
                                <span>{!!used ? <span style={{color: 'var(--green)'}}>{used}</span> : null}<br/>{limit - used}</span>
                            </TableCell>
                        </Fragment>;
                    });
                })}
            </TableRow>);
        }
        const foodStyle: CSSProperties = {width: ((90 / (days.length + foodServiceTypes.length)) / 2) + '%', textAlign: 'center'};

        return <Grid key={pp.title} item xs={12}>
            <Card sx={cateringStyle}>
                <CardHeader title={<span>{pp.title}{!!pp.capacity && pp.capacity < 9999 && <span style={{float: 'right'}}>{pp.capacity}</span>}</span>}/>
                <CardContent>
                    <Table className={'data-grid'}>
                        <TableHead>
                            <TableRow>
                                <TableCell style={{width: '8%'}} rowSpan={2}><span>{t('Dny')}</span></TableCell>
                                {days?.map((d, i) => {
                                    return <TableCell key={d.dayNo} style={{width: (90 / days.length) + '%', textAlign: 'center'}}
                                        colSpan={foodServiceTypes.length * 2} className={i % 2 > 0 ? 'high-col' : undefined}>
                                        <span>{dateToGuiAs(d.dayDate, "eeeee d.M.")}</span>
                                    </TableCell>;
                                })}
                            </TableRow>
                            <TableRow>
                                {days?.map((d, i) => {
                                    const cn = i % 2 > 0 ? 'high-col' : undefined;
                                    return foodServiceTypes.map((fs) => {
                                        return <Fragment key={d.dayNo + '-' + fs.value}>
                                            <TableCell style={foodStyle}
                                                className={cn}>
                                                <span>{fs.label}</span>
                                            </TableCell>
                                            <TableCell style={foodStyle}
                                                className={cn}>
                                                <span></span>
                                            </TableCell>
                                        </Fragment>;
                                    })
                                })}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {rows}
                        </TableBody>
                    </Table>
                </CardContent>
            </Card>
        </Grid>
    }, [eventId, pp, days, foodServiceTypes, groups, getCateringInfo, t]);
}

const EventReportCatering = ({eventId, eventDays}: { eventId: number, eventDays: JsonEventDayInfo[] }) => {
    const t = useAppTranslation();
    const dispatch = useAppDispatch();
    const codebooks = useAppSelector<CodebookState>(selectCodebooks);
    const placeFoodList = codebooks['placeFood'];
    const groupList = codebooks['group'];

    const [eventParties, setEventParties] = useState<JsonEventPartyInfo[] | undefined>();
    const [placeInfos, setPlaceInfos] = useState<JsonPlaceInfo[] | undefined>();
    const [eventCatering, setEventCatering] = useState<JsonEventCateringData | undefined>();
    const [foodServiceTypes, setFoodServiceTypes] = useState<OptionValue[]>([]);
    const [groups, setGroups] = useState<JsonGroupInfo[]>([]);

    const handleFetchItems = useCallback(async () => {
        setEventParties(undefined);
        const event = getApiResult(await dispatch(fetchEvent(eventId)));
        if (!event) {
            return;
        }
        const places = getApiResult(await dispatch(fetchPlacesOnline({eventId})));
        const groups = getApiResult(await dispatch(fetchGroups({})));

        setEventParties(getApiResult(await dispatch(fetchEventPartiesOnline({
            orderCol: 'fullName',
            rows: 10000,
            eventId,
            foodIds: [FAKE_VALUE_ALL],
            statuses: [GetEventPartyListUsingGETStatusesEnum.Pending, GetEventPartyListUsingGETStatusesEnum.Active]
        }))));
        setPlaceInfos(places);
        setEventCatering(event.cateringData)
        setFoodServiceTypes(foodServiceOptions.filter(o => !event.eventData?.foodServiceTypes
            || event.eventData.foodServiceTypes.indexOf(o.value as JsonEventDataFoodServiceTypesEnum) >= 0));
        setGroups(groups!);
    }, [eventId, dispatch]);

    const foodPlaces = useMemo(() => {
        const foodPlaces: CateringPlace[] = [];
        if (!eventParties || !placeFoodList || !groupList || !eventDays || !placeInfos) {
            return foodPlaces;
        }

        const options = createOptions({codebookName: 'placeFood', scope: eventId, asNumber: true}, {placeFood: placeFoodList});
        options.push(createOption(FAKE_VALUE_ALL, t('Celkem')));
        options.forEach((o) => {
            const p = placeInfos?.find((p) => p.placeId === o.value);
            const foodPlace: CateringPlace = {
                placeId: o.value as number,
                title: p?.title || o.label || o.value + '',
                capacity: p?.foodCapacity,
                groups: []
            }

            const groupsById: { [key in number]: CateringPlaceGroup } = {};

            eventParties.forEach((ep) => {
                ep.eventDayDetails?.forEach((dd) => {
                    if (!dd['foodBrId'] && !dd['foodLuId'] && !dd['foodDiId'] && !dd['foodSnId']) {
                        return;
                    }
                    if ((o.value === FAKE_VALUE_ALL
                            || dd['foodBrId'] === o.value
                            || dd['foodLuId'] === o.value
                            || dd['foodDiId'] === o.value
                            || dd['foodSnId'] === o.value)
                        && dd.dayNo
                        && ep.eventDays?.[dd.dayNo - 1] === '1') {
                        const groupId = ep.groupId || FAKE_VALUE_EMPTY;
                        if (!groupsById[groupId]) {
                            const g = groupList.find((o) => o.value === '' + groupId);
                            groupsById[groupId] = {
                                groupId,
                                title: g?.label || t('Bez skupiny'),
                                days: {}
                            }
                            foodPlace.groups.push(groupsById[groupId]);
                        }
                        const g = groupsById[groupId];
                        if (!g.days[dd.dayNo]) {
                            g.days[dd.dayNo] = {
                                items: [],
                                limits: []
                            }
                        }
                        g.days[dd.dayNo].items.push(ep);
                    }
                })
            });

            eventCatering?.items?.forEach((ec) => {
                if ((ec.placeId === o.value || o.value === FAKE_VALUE_ALL) && ec.groupId) {
                    const groupId = ec.groupId;
                    if (!groupsById[groupId]) {
                        const g = groupList.find((o) => o.value === '' + groupId);
                        groupsById[groupId] = {
                            groupId,
                            title: g?.label || t('Bez skupiny'),
                            days: {}
                        }
                        foodPlace.groups.push(groupsById[groupId]);
                    }
                    const g = groupsById[groupId];
                    const ed = eventDays.find(ed => ed.eventDayId === ec.eventDayId);
                    if (!ed?.dayNo) {
                        return;
                    }
                    if (!g.days[ed.dayNo]) {
                        g.days[ed.dayNo] = {
                            items: [],
                            limits: []
                        }
                    }
                    g.days[ed.dayNo].limits.push(ec);
                }
            });

            foodPlaces.push(foodPlace);
        })
        foodPlaces.forEach((fp) => {
            fp.groups = fp.groups.sort((a, b) => localCompare(a.title, b.title))
        })
        return foodPlaces;

    }, [eventId, eventCatering, eventParties, placeFoodList, groupList, eventDays, placeInfos, t]);

    useEffect(() => {
        handleFetchItems().then();
    }, [eventId, handleFetchItems]);

    const isLoading = !eventDays || !placeFoodList || !eventParties || !groupList || !placeInfos;
    return isLoading
        ? <LinearProgress/>
        : <Grid container spacing={2}>
            {foodPlaces.map((pp) => {
                return <EventFoodPlace key={pp.title} eventId={eventId} pp={pp} days={eventDays} foodServiceTypes={foodServiceTypes} groups={groups}/>;
            })}
        </Grid>;
}

export default EventReportCatering;
